/**
 * Redux slice (src/store/slices/slotSlice.js).
 *
 * Data flow (typical):
 * - UI dispatches actions/thunks from `src/store/actions/*`.
 * - `reducers` handles synchronous state updates.
 * - `extraReducers` reacts to thunk pending/fulfilled/rejected actions.
 */

import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { t } from "../../utils/i18nTranslate";
import {
  createTeacherAvailability,
  getTeacherAvailability,
  bookSlot,
  cancelBooking,
  rescheduleBooking,
  completeBooking,
  updateAttendance,
  getUpcomingBookings,
  getBookingHistory,
  getBookingDetails,
  searchBookings,
  migrateOldBookings,
  teacherJoinSession,
  studentJoinSession
} from "../actions/slotActions";

/**
 * Slice initial state.
 *
 * Keep this shape stable because it is consumed across many selectors/components.
 */
const initialState = {
  // Loading States
  loading: false,
  availabilityLoading: false,
  bookingLoading: false,
  upcomingBookingsLoading: false,
  historyLoading: false,
  searchLoading: false,
  sessionLoading: false,
  
  // Data States
  teacherAvailability: [],
  upcomingBookings: [],
  bookingHistory: [],
  currentBooking: null,
  searchResults: [],
  
  // Session States
  teacherSessionActive: false,
  studentSessionActive: false,
  sessionDetails: null,
  
  // Migration
  migrationStats: null,
  
  // Pagination
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  },
  
  // Filters & Preferences
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  
  // Errors
  error: null,
  lastAction: null
};

/**
 * RTK slice definition: slotSlice.
 */
const slotSlice = createSlice({
  name: "slots",
  initialState,
  reducers: {
    /**
     * Reducer: setTimezone.
     */
    setTimezone: (state, action) => {
      state.timezone = action.payload;
    },
    /**
     * Reducer: clearCurrentBooking.
     */
    clearCurrentBooking: (state) => {
      state.currentBooking = null;
    },
    /**
     * Reducer: clearErrors.
     */
    clearErrors: (state) => {
      state.error = null;
    }
  },
  /**
   * Reducer: extraReducers.
   */
  extraReducers: (builder) => {
    builder
      // Create Teacher Availability
      .addCase(createTeacherAvailability.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.lastAction = "createTeacherAvailability";
      })
      // extraReducers handler: createTeacherAvailability.fulfilled
      .addCase(createTeacherAvailability.fulfilled, (state, action) => {
        state.loading = false;
        // If we've already loaded teacher availability, add the new slots
        if (state.teacherAvailability.length > 0 && action.payload.data) {
          state.teacherAvailability = [...state.teacherAvailability, ...action.payload.data];
        }
        toast.success(t("store.slot.availabilityCreated"));
      })
      // extraReducers handler: createTeacherAvailability.rejected
      .addCase(createTeacherAvailability.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create availability slots";
        toast.error(state.error);
      })

      // Get Teacher Availability
      .addCase(getTeacherAvailability.pending, (state) => {
        state.availabilityLoading = true;
        state.error = null;
        state.lastAction = "getTeacherAvailability";
      })
      // extraReducers handler: getTeacherAvailability.fulfilled
      .addCase(getTeacherAvailability.fulfilled, (state, action) => {
        state.availabilityLoading = false;
        if (action.payload && action.payload.data) {
          state.teacherAvailability = action.payload.data;
          if (action.payload.count !== undefined) {
            state.pagination.totalItems = action.payload.count;
          }
        } else {
          state.teacherAvailability = [];
        }
      })
      // extraReducers handler: getTeacherAvailability.rejected
      .addCase(getTeacherAvailability.rejected, (state, action) => {
        state.availabilityLoading = false;
        state.error = action.payload || "Failed to fetch teacher availability";
        toast.error(state.error);
      })

      // Book Slot
      .addCase(bookSlot.pending, (state) => {
        state.bookingLoading = true;
        state.error = null;
        state.lastAction = "bookSlot";
      })
      // extraReducers handler: bookSlot.fulfilled
      .addCase(bookSlot.fulfilled, (state, action) => {
        state.bookingLoading = false;
        
        // Update availability if we're viewing that
        if (state.teacherAvailability.length > 0) {
          const slotId = action.meta.arg.slotId;
          const slotIndex = state.teacherAvailability.findIndex(slot => slot._id === slotId);
          
          if (slotIndex !== -1) {
            // Mark slot as booked or remove from available slots
            state.teacherAvailability = state.teacherAvailability.filter(slot => slot._id !== slotId);
          }
        }
        
        toast.success(t("store.slot.bookSuccessfully"));
      })
      // extraReducers handler: bookSlot.rejected
      .addCase(bookSlot.rejected, (state, action) => {
        state.bookingLoading = false;
        state.error = action.payload || "Failed to book slot";
        toast.error(state.error);
      })

      // Cancel Booking
      .addCase(cancelBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.lastAction = "cancelBooking";
      })
      // extraReducers handler: cancelBooking.fulfilled
      .addCase(cancelBooking.fulfilled, (state, action) => {
        state.loading = false;
        
        // Update current booking if we're viewing it
        if (state.currentBooking && state.currentBooking._id === action.meta.arg.bookingId) {
          state.currentBooking = {
            ...state.currentBooking,
            status: 'cancelled',
            cancellationDetails: {
              cancelledBy: action.meta.arg.cancelledBy || 'system',
              reason: action.meta.arg.reason || 'No reason provided',
              cancelledAt: new Date().toISOString()
            }
          };
        }
        
        // Update upcoming bookings if we're viewing them
        if (state.upcomingBookings.length > 0) {
          const bookingId = action.meta.arg.bookingId;
          state.upcomingBookings = state.upcomingBookings.filter(booking => booking._id !== bookingId);
        }
        
        toast.success(t("store.slot.bookingCancelled"));
      })
      // extraReducers handler: cancelBooking.rejected
      .addCase(cancelBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to cancel booking";
        toast.error(state.error);
      })

      // Reschedule Booking
      .addCase(rescheduleBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.lastAction = "rescheduleBooking";
      })
      // extraReducers handler: rescheduleBooking.fulfilled
      .addCase(rescheduleBooking.fulfilled, (state, action) => {
        state.loading = false;
        
        // Update current booking if we're viewing it
        if (state.currentBooking && state.currentBooking._id === action.meta.arg.bookingId) {
          // The response should include the updated booking
          if (action.payload && action.payload.data) {
            state.currentBooking = action.payload.data;
          } else {
            state.currentBooking = {
              ...state.currentBooking,
              status: 'rescheduled',
              reschedulingDetails: {
                rescheduledBy: action.meta.arg.rescheduledBy || 'system',
                reason: action.meta.arg.reason || 'No reason provided',
                rescheduledAt: new Date().toISOString(),
                originalSlotId: state.currentBooking.slotId,
                newSlotId: action.meta.arg.newSlotId
              }
            };
          }
        }
        
        // Update upcoming bookings if we're viewing them
        if (state.upcomingBookings.length > 0 && action.payload && action.payload.data) {
          const bookingId = action.meta.arg.bookingId;
          const bookingIndex = state.upcomingBookings.findIndex(booking => booking._id === bookingId);
          
          if (bookingIndex !== -1) {
            state.upcomingBookings[bookingIndex] = action.payload.data;
          }
        }
        
        toast.success(t("store.slot.bookingRescheduled"));
      })
      // extraReducers handler: rescheduleBooking.rejected
      .addCase(rescheduleBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to reschedule booking";
        toast.error(state.error);
      })

      // Complete Booking
      .addCase(completeBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.lastAction = "completeBooking";
      })
      // extraReducers handler: completeBooking.fulfilled
      .addCase(completeBooking.fulfilled, (state, action) => {
        state.loading = false;
        
        // Update current booking if we're viewing it
        if (state.currentBooking && state.currentBooking._id === action.meta.arg.bookingId) {
          // The response should include the updated booking
          if (action.payload && action.payload.data) {
            state.currentBooking = action.payload.data;
          } else {
            state.currentBooking = {
              ...state.currentBooking,
              status: 'completed',
              completionDetails: {
                completedBy: action.meta.arg.completedBy || 'system',
                teacherNotes: action.meta.arg.teacherNotes,
                completedAt: new Date().toISOString()
              }
            };
          }
        }
        
        // Update upcoming bookings if we're viewing them
        if (state.upcomingBookings.length > 0) {
          const bookingId = action.meta.arg.bookingId;
          // Remove from upcoming and move to history if necessary
          state.upcomingBookings = state.upcomingBookings.filter(booking => booking._id !== bookingId);
          
          if (action.payload && action.payload.data && state.bookingHistory.length > 0) {
            state.bookingHistory = [action.payload.data, ...state.bookingHistory];
          }
        }
        
        toast.success(t("store.slot.bookingCompleted"));
      })
      // extraReducers handler: completeBooking.rejected
      .addCase(completeBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to complete booking";
        toast.error(state.error);
      })

      // Update Attendance
      .addCase(updateAttendance.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.lastAction = "updateAttendance";
      })
      // extraReducers handler: updateAttendance.fulfilled
      .addCase(updateAttendance.fulfilled, (state, action) => {
        state.loading = false;
        
        // Update current booking if we're viewing it
        if (state.currentBooking && state.currentBooking._id === action.meta.arg.bookingId) {
          if (action.payload && action.payload.data) {
            state.currentBooking = action.payload.data;
          } else {
            const attendanceField = action.meta.arg.attendeeType === 'teacher' 
              ? 'teacherAttended' 
              : 'studentAttended';
            
            state.currentBooking = {
              ...state.currentBooking,
              [attendanceField]: action.meta.arg.status
            };
          }
        }
        
        toast.success(t("store.slot.attendanceUpdated", { attendeeType: action.meta.arg.attendeeType }));
      })
      // extraReducers handler: updateAttendance.rejected
      .addCase(updateAttendance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update attendance";
        toast.error(state.error);
      })

      // Get Upcoming Bookings
      .addCase(getUpcomingBookings.pending, (state) => {
        state.upcomingBookingsLoading = true;
        state.error = null;
        state.lastAction = "getUpcomingBookings";
      })
      // extraReducers handler: getUpcomingBookings.fulfilled
      .addCase(getUpcomingBookings.fulfilled, (state, action) => {
        state.upcomingBookingsLoading = false;
        if (action.payload && action.payload.data) {
          state.upcomingBookings = action.payload.data;
          if (action.payload.pagination) {
            state.pagination = action.payload.pagination;
          } else if (action.payload.count !== undefined) {
            state.pagination.totalItems = action.payload.count;
          }
        } else {
          state.upcomingBookings = [];
        }
      })
      // extraReducers handler: getUpcomingBookings.rejected
      .addCase(getUpcomingBookings.rejected, (state, action) => {
        state.upcomingBookingsLoading = false;
        state.error = action.payload || "Failed to fetch upcoming bookings";
        toast.error(state.error);
      })

      // Get Booking History
      .addCase(getBookingHistory.pending, (state) => {
        state.historyLoading = true;
        state.error = null;
        state.lastAction = "getBookingHistory";
      })
      // extraReducers handler: getBookingHistory.fulfilled
      .addCase(getBookingHistory.fulfilled, (state, action) => {
        state.historyLoading = false;
        if (action.payload && action.payload.data) {
          state.bookingHistory = action.payload.data;
          if (action.payload.pagination) {
            state.pagination = action.payload.pagination;
          } else if (action.payload.count !== undefined) {
            state.pagination.totalItems = action.payload.count;
          }
        } else {
          state.bookingHistory = [];
        }
      })
      // extraReducers handler: getBookingHistory.rejected
      .addCase(getBookingHistory.rejected, (state, action) => {
        state.historyLoading = false;
        state.error = action.payload || "Failed to fetch booking history";
        toast.error(state.error);
      })

      // Get Booking Details
      .addCase(getBookingDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.lastAction = "getBookingDetails";
      })
      // extraReducers handler: getBookingDetails.fulfilled
      .addCase(getBookingDetails.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload && action.payload.data) {
          state.currentBooking = action.payload.data;
        } else {
          state.currentBooking = null;
        }
      })
      // extraReducers handler: getBookingDetails.rejected
      .addCase(getBookingDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch booking details";
        toast.error(state.error);
      })

      // Search Bookings
      .addCase(searchBookings.pending, (state) => {
        state.searchLoading = true;
        state.error = null;
        state.lastAction = "searchBookings";
      })
      // extraReducers handler: searchBookings.fulfilled
      .addCase(searchBookings.fulfilled, (state, action) => {
        state.searchLoading = false;
        if (action.payload && action.payload.data) {
          state.searchResults = action.payload.data;
          if (action.payload.pagination) {
            state.pagination = action.payload.pagination;
          } else if (action.payload.count !== undefined) {
            state.pagination.totalItems = action.payload.count;
          }
        } else {
          state.searchResults = [];
        }
      })
      // extraReducers handler: searchBookings.rejected
      .addCase(searchBookings.rejected, (state, action) => {
        state.searchLoading = false;
        state.error = action.payload || "Failed to search bookings";
        toast.error(state.error);
      })

      // Migrate Old Bookings
      .addCase(migrateOldBookings.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.lastAction = "migrateOldBookings";
      })
      // extraReducers handler: migrateOldBookings.fulfilled
      .addCase(migrateOldBookings.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload && action.payload.data) {
          state.migrationStats = action.payload.data;
        }
        toast.success(t("store.slot.oldBookingsMigrated"));
      })
      // extraReducers handler: migrateOldBookings.rejected
      .addCase(migrateOldBookings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to migrate old bookings";
        toast.error(state.error);
      })

      // Teacher Join Session
      .addCase(teacherJoinSession.pending, (state) => {
        state.sessionLoading = true;
        state.error = null;
        state.lastAction = "teacherJoinSession";
      })
      // extraReducers handler: teacherJoinSession.fulfilled
      .addCase(teacherJoinSession.fulfilled, (state, action) => {
        state.sessionLoading = false;
        // Preserve actual attendance until connection/status confirms it.
        if (action.meta?.arg?.launchOnly === true) return;
        state.teacherSessionActive = true;
        if (action.payload && action.payload.data) {
          state.sessionDetails = action.payload.data;
        }
      })
      // extraReducers handler: teacherJoinSession.rejected
      .addCase(teacherJoinSession.rejected, (state, action) => {
        state.sessionLoading = false;
        state.error = action.payload || "Failed to join teacher session";
        toast.error(state.error);
      })

      // Student Join Session
      .addCase(studentJoinSession.pending, (state) => {
        state.sessionLoading = true;
        state.error = null;
        state.lastAction = "studentJoinSession";
      })
      // extraReducers handler: studentJoinSession.fulfilled
      .addCase(studentJoinSession.fulfilled, (state, action) => {
        state.sessionLoading = false;
        if (action.meta?.arg?.launchOnly === true) return;
        state.studentSessionActive = true;
        if (action.payload && action.payload.data) {
          state.sessionDetails = action.payload.data;
        }
      })
      // extraReducers handler: studentJoinSession.rejected
      .addCase(studentJoinSession.rejected, (state, action) => {
        state.sessionLoading = false;
        state.error = action.payload || "Failed to join student session";
        toast.error(state.error);
      });
  }
});

export const { setTimezone, clearCurrentBooking, clearErrors } = slotSlice.actions;

export default slotSlice.reducer;
